import ResultModal, { IResultModalProps } from 'pages/Assessment/screens/ResultModal';
import { createContext, FC, ReactNode, useContext, useMemo, useState } from 'react';

type ResultModalProps = Omit<IResultModalProps, 'isOpen' | 'onClose'> & {
  onClose?: () => void;
};

interface DialogContextProps {
  showResultModal: (props: ResultModalProps) => void;
  closeDialog: () => void;
  dialogProps: ResultModalProps | null;
}

const ResultModalContext = createContext<DialogContextProps | undefined>(undefined);

const ResultModalProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [dialogProps, setDialogProps] = useState<ResultModalProps | null>(null);

  const showResultModal = (props: ResultModalProps) => {
    setDialogProps(props);
  };

  const closeDialog = () => {
    setDialogProps(null);
  };

  const contextValue = useMemo(
    () => ({ showResultModal, closeDialog, dialogProps }),
    [showResultModal, closeDialog, dialogProps],
  );

  return (
    <ResultModalContext.Provider value={contextValue}>
      {children}
      {dialogProps && (
        <ResultModal
          {...dialogProps}
          isOpen={!!dialogProps}
          onClose={() => {
            dialogProps?.onClose?.();
            closeDialog();
          }}
        />
      )}
    </ResultModalContext.Provider>
  );
};

const useResultModal = (): DialogContextProps => {
  const context = useContext(ResultModalContext);
  if (context === undefined) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

export { ResultModalProvider, useResultModal };
